import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Button } from '../components/common/Button';
import { StoryList } from '../components/StoryList';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';
import { BookCarousel } from '../components/BookCarousel';
import { Cat } from 'lucide-react';
import { DragonIcon, AstronautIcon } from '../components/common/CustomIcons';

const API_URL = process.env.REACT_APP_API_URL || '';

/* ───── Animations ───── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

/*
 * Entrance animation that is safe by default:
 * - content is visible (opacity: 1) unless the user's environment
 *   supports and prefers motion, in which case it animates in.
 * - avoids the "invisible content" failure mode of opacity: 0 + forwards.
 */
const entrance = css<{ $index?: number }>`
  @media (prefers-reduced-motion: no-preference) {
    opacity: 0;
    animation: ${fadeInUp} 0.5s ease forwards;
    animation-delay: ${({ $index }) => ($index ?? 0) * 0.08}s;
  }
`;

/* ───── Layout ───── */
const HomeContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
`;

/* ───── Hero ───── */
const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  text-align: center;
  margin-bottom: 24px;
  background-color: #f8f9fa;
  border-radius: 16px;
  padding: 48px 32px;

  @media (min-width: 900px) {
    flex-direction: row;
    text-align: left;
    padding: 56px 48px;
  }
`;

const HeroContent = styled.div`
  flex: 1 1 55%;
  min-width: 0;
`;

const HeroVisual = styled.div`
  flex: 1 1 45%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const HeroLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${COLORS.PRIMARY};
  margin-bottom: 12px;
`;

const HeroTitle = styled.h1`
  color: ${COLORS.PRIMARY};
  margin: 0 0 16px 0;
  font-family: 'Cormorant Garamond', Georgia, 'Times New Roman', serif;
  font-size: 2.2rem;
  font-weight: 700;
  line-height: 1.15;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.15rem;
  color: #495057;
  margin: 0 0 8px 0;
  line-height: 1.6;
  max-width: 34em;
`;

/* ─── Hero: secondary paths ───
 * Reading the library and buying a ready-made book stay one click away,
 * but as subordinate text links. The hero has to read as a single idea —
 * "this is something you do with your child" — before it offers anything else.
 */
const SecondaryPaths = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin-top: 20px;
  justify-content: center;

  @media (min-width: 900px) {
    justify-content: flex-start;
  }
`;

const SecondaryLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.9rem;
  color: #6c757d;
  transition: color 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: ${COLORS.PRIMARY};
    text-decoration: underline;
  }

  &:hover svg {
    transform: translateX(3px);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

const RatingRow = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0 0 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.95rem;
  color: #495057;

  &:hover span:last-child {
    text-decoration: underline;
  }
`;

const RatingStars = styled.span`
  color: #f5a623;
  letter-spacing: 2px;
  font-size: 1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 28px;

  @media (min-width: 900px) {
    align-items: flex-start;
  }
`;

/*
 * Makes the invisible incentive visible: tells the visitor exactly
 * what happens when they click the primary CTA (account -> 10 credits).
 */
const CreditsNote = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
`;

const TrustPoints = styled.ul`
  list-style: none;
  display: flex;
  gap: 24px;
  margin: 28px 0 0 0;
  padding: 0;
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
  flex-wrap: wrap;
  justify-content: center;

  @media (min-width: 900px) {
    justify-content: flex-start;
  }
`;

const TrustPoint = styled.li`
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '✓';
    color: ${COLORS.PRIMARY};
    font-weight: 700;
  }
`;

/* ─── Hero: live story setup ───
 * Deliberately mirrors the first two questions you'd actually ask your
 * child ("what's your name in it?", "who's the hero?"), so the visual
 * dramatises the shared activity rather than only teasing the product.
 */
const NameInputLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
`;

const HeroPrompt = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  text-align: center;
`;

const HeroChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  max-width: 300px;
`;

const HeroChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;

  /* Overrides the width/height attributes lucide sets on its own icons,
     so every chip icon renders at the same size. */
  svg {
    width: 17px;
    height: 17px;
    flex-shrink: 0;
  }

  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1.5px solid ${props => (props.$active ? COLORS.PRIMARY : '#dee2e6')};
  background: ${props => (props.$active ? COLORS.PRIMARY : '#fff')};
  color: ${props => (props.$active ? '#fff' : '#495057')};

  &:hover {
    border-color: ${COLORS.PRIMARY};
    color: ${props => (props.$active ? '#fff' : COLORS.PRIMARY)};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: 2px;
  }
`;

const NameInput = styled.input`
  width: 100%;
  max-width: 280px;
  padding: 12px 16px;
  border: 1.5px solid #dee2e6;
  border-radius: 10px;
  font-size: 1rem;
  text-align: center;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 0 0 3px rgba(118, 75, 162, 0.15);
  }
`;

const BookCoverPreview = styled.div`
  width: 240px;
  aspect-ratio: 3 / 4;
  border-radius: 6px 14px 14px 6px;
  background: linear-gradient(150deg, ${COLORS.PRIMARY} 0%, ${COLORS.PRIMARY_LIGHT} 130%);
  box-shadow:
    inset 8px 0 12px -8px rgba(0, 0, 0, 0.35),
    0 16px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 22px;
  box-sizing: border-box;
  position: relative;
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-4px) rotate(-1deg);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 10px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 4px 10px 10px 4px;
    pointer-events: none;
  }
`;

/*
 * Caption under the preview cover: lets the visual carry part of the
 * explanation (read free / printed & delivered) instead of only
 * reinforcing the "create" path.
 */
const CoverCaption = styled.div`
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
  text-align: center;
`;

const CoverKicker = styled.div`
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 14px;
`;

const CoverTitle = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  text-align: center;
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

const CoverStars = styled.div`
  margin-top: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  letter-spacing: 8px;
`;

/* ───── Proof strip ───── */
const ProofStrip = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px 40px;
  flex-wrap: wrap;
  padding: 20px 16px;
  margin-bottom: 56px;
  font-size: 0.9rem;
  color: #495057;
  border-bottom: 1px solid #e9ecef;
`;

const ProofItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
`;

/* ───── Sections ───── */
const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 8px 0;
  text-align: center;
  font-family: 'Cormorant Garamond', Georgia, serif;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: #6c757d;
  font-size: 1rem;
  margin: 0 auto 32px;
  max-width: 40em;
  line-height: 1.6;
`;

/* ─── How It Works ─── */
const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled.div<{ $index?: number }>`
  text-align: center;
  padding: 40px 28px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  ${entrance}

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  }
`;

const StepNumber = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${COLORS.PRIMARY};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.3rem;
  margin: 0 auto 20px;
`;

const StepTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 10px 0;
`;

const StepDescription = styled.p`
  color: #6c757d;
  line-height: 1.6;
  font-size: 0.95rem;
  margin: 0;
`;

const SectionCTARow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 32px;
`;

/* ─── Print ladder ───
 * The three ways to end up holding a physical book, deliberately unequal
 * in weight: your own stories first, library stories second, ready-made
 * last. Card sizes encode the priority so the ranking is readable at a
 * glance without anyone having to read the copy.
 */
const LadderGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 20px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const LadderCard = styled.div<{ $featured?: boolean; $index?: number }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: ${props => (props.$featured ? '32px 28px' : '24px 20px')};
  background: #fff;
  border-radius: 16px;
  border: 1.5px solid ${props => (props.$featured ? COLORS.PRIMARY : '#e9ecef')};
  box-shadow: ${props =>
    props.$featured
      ? '0 12px 28px rgba(118, 75, 162, 0.14)'
      : '0 2px 8px rgba(0, 0, 0, 0.04)'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  ${entrance}

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${props =>
      props.$featured
        ? '0 16px 34px rgba(118, 75, 162, 0.2)'
        : '0 8px 18px rgba(0, 0, 0, 0.08)'};
  }
`;

const LadderBadge = styled.span`
  position: absolute;
  top: -11px;
  left: 28px;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${COLORS.PRIMARY};
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const LadderIconWrap = styled.span<{ $featured?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => (props.$featured ? '48px' : '38px')};
  height: ${props => (props.$featured ? '48px' : '38px')};
  border-radius: 12px;
  background: rgba(118, 75, 162, 0.1);
  color: ${COLORS.PRIMARY};

  svg {
    width: ${props => (props.$featured ? '26px' : '20px')};
    height: ${props => (props.$featured ? '26px' : '20px')};
  }
`;

const LadderStepLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #adb5bd;
`;

const LadderTitle = styled.h3<{ $featured?: boolean }>`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: ${props => (props.$featured ? '1.7rem' : '1.2rem')};
  font-weight: 700;
  color: ${props => (props.$featured ? COLORS.PRIMARY : '#212529')};
  margin: 0;
  line-height: 1.2;
`;

const LadderDesc = styled.p`
  font-size: 0.9rem;
  color: #6c757d;
  line-height: 1.55;
  margin: 0 0 4px 0;
  flex: 1;
`;

const LadderLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  margin-top: auto;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${COLORS.PRIMARY};

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.2s ease;
  }

  &:hover {
    text-decoration: underline;
  }

  &:hover svg {
    transform: translateX(3px);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

const LadderCTARow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: auto;
  padding-top: 8px;
`;

const LadderNote = styled.span`
  font-size: 0.8rem;
  color: #868e96;
`;

/* ─── Ready-made books ─── */
const CountryFilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const CountryFilterLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
  margin-right: 2px;
  white-space: nowrap;
`;

const CountryChip = styled.button<{ $active: boolean }>`
  padding: 4px 14px;
  border-radius: 20px;
  border: 1.5px solid ${({ $active }) => ($active ? COLORS.PRIMARY : '#dee2e6')};
  background: ${({ $active }) => ($active ? COLORS.PRIMARY : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : '#495057')};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    background: ${({ $active }) => ($active ? COLORS.PRIMARY : 'rgba(118, 75, 162, 0.08)')};
  }
`;

const NoBooksMessage = styled.p`
  text-align: center;
  color: #6c757d;
  font-size: 1.1rem;
  padding: 40px 20px;
  font-style: italic;
`;

const CarouselSkeleton = styled.div`
  height: 280px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f3f5 25%, #e9ecef 50%, #f1f3f5 75%);
  background-size: 800px 100%;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${shimmer} 1.4s linear infinite;
  }
`;

/* ─── Reviews ─── */
const AggregateRating = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 32px;
  font-size: 1rem;
  color: #495057;
  font-weight: 600;
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
`;

const ReviewCard = styled.div<{ $index?: number }>`
  background-color: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  ${entrance}

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  }
`;

const QuoteIcon = styled.div`
  color: ${COLORS.PRIMARY_LIGHT};
  margin-bottom: 12px;
  font-size: 2rem;
  line-height: 1;
  font-family: Georgia, serif;
`;

const ReviewText = styled.p`
  font-size: 1rem;
  color: #495057;
  margin: 0 0 16px 0;
  line-height: 1.6;
  font-style: italic;
`;

const ReviewAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AuthorAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${COLORS.PRIMARY};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.span`
  font-weight: 600;
  color: #212529;
  font-size: 0.95rem;
`;

const StarRow = styled.div`
  display: flex;
  gap: 2px;
  margin-top: 2px;
`;

const Star = styled.span<{ $filled: boolean }>`
  color: ${({ $filled }) => ($filled ? '#f5a623' : '#ddd')};
  font-size: 0.85rem;
`;

/* ─── Add Review Card ─── */
const AddReviewCard = styled.div<{ $index?: number }>`
  background-color: #fff;
  border: 2px dashed ${COLORS.PRIMARY_LIGHT};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
  min-height: 200px;
  ${entrance}

  &:hover {
    transform: translateY(-4px);
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 16px rgba(118, 75, 162, 0.12);
  }
`;

const AddIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${COLORS.PRIMARY_LIGHT};
  color: ${COLORS.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const AddLabel = styled.span`
  font-size: 1.05rem;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
`;

/* ─── Add Review Modal ─── */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);

  @media (prefers-reduced-motion: no-preference) {
    animation: ${fadeInUp} 0.3s ease;
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 24px 0;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 6px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 0 0 3px rgba(118, 75, 162, 0.15);
  }
`;

const StarRatingInput = styled.div`
  display: flex;
  gap: 4px;
`;

const StarButton = styled.button<{ $filled: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  font-size: 1.5rem;
  color: ${({ $filled }) => ($filled ? '#f5a623' : '#ddd')};
  transition: color 0.15s, transform 0.15s;

  &:hover {
    transform: scale(1.2);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ErrorText = styled.p`
  color: #dc3545;
  font-size: 0.85rem;
  margin: 4px 0 0 0;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 20px 0;

  svg {
    width: 48px;
    height: 48px;
    color: ${COLORS.PRIMARY};
    margin-bottom: 12px;
  }

  p {
    font-size: 1.1rem;
    color: #495057;
    margin: 0;
  }
`;

/* ─── FAQ ─── */
const FAQGrid = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FAQItem = styled.div<{ $index?: number }>`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
  transition: box-shadow 0.2s ease;
  ${entrance}

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const FAQQuestion = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: ${(props) => (props.$isOpen ? '#f8f9fa' : 'transparent')};
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 1rem;
  font-weight: 600;
  color: #212529;
  line-height: 1.4;
  transition: background 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }

  @media (max-width: 576px) {
    padding: 16px 18px;
    font-size: 0.95rem;
  }
`;

const FAQIcon = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(props) => (props.$isOpen ? COLORS.PRIMARY : '#e9ecef')};
  color: ${(props) => (props.$isOpen ? 'white' : '#495057')};
  flex-shrink: 0;
  transition: all 0.25s ease;

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.25s ease;
    transform: ${(props) => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  }
`;

const FAQAnswer = styled.div<{ $isOpen: boolean }>`
  max-height: ${(props) => (props.$isOpen ? '500px' : '0')};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  overflow: hidden;
  transition: max-height 0.35s ease, opacity 0.25s ease, padding 0.25s ease;
  padding: ${(props) => (props.$isOpen ? '0 24px 20px' : '0 24px')};

  @media (max-width: 576px) {
    padding: ${(props) => (props.$isOpen ? '0 18px 16px' : '0 18px')};
  }
`;

const FAQAnswerText = styled.p`
  font-size: 0.95rem;
  color: #6c757d;
  line-height: 1.7;
  margin: 0;
`;

/* ─── Closing CTA ─── */
const ClosingCTA = styled.section`
  background: ${COLORS.PRIMARY};
  border-radius: 16px;
  padding: 56px 32px;
  text-align: center;
  margin-bottom: 24px;
`;

const ClosingTitle = styled.h2`
  color: #fff;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 12px 0;

  @media (min-width: 768px) {
    font-size: 2.4rem;
  }
`;

const ClosingSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.05rem;
  margin: 0 auto 28px;
  max-width: 34em;
  line-height: 1.6;
`;

const ClosingButton = styled.button`
  background: #fff;
  color: ${COLORS.PRIMARY};
  border: none;
  border-radius: 10px;
  padding: 14px 32px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  }
`;

const ClosingNote = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  margin: 14px 0 0 0;
`;

/* ───── Interfaces ───── */
interface FeaturedBook {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  imageUrl: string;
  allowedCountries?: string[];
}

interface Review {
  _id: string;
  userName: string;
  text: string;
  rating: number;
  createdAt: string;
}

/* ───── Path icons (custom, stroke-based) ───── */
const iconProps = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

/* Pencil writing, with a small sparkle — stories you made yourself */
const OwnStoriesIcon: React.FC = () => (
  <svg {...iconProps}>
    <path d="M17 3.5a2.1 2.1 0 0 1 3 3L8.5 18 4 19l1-4.5Z" />
    <path d="M13.5 6.5l3.5 3.5" />
    <path d="M19 14v4" />
    <path d="M17 16h4" />
  </svg>
);

/* Open book — the public library */
const LibraryIcon: React.FC = () => (
  <svg {...iconProps}>
    <path d="M2 5h6a4 4 0 0 1 4 4v11a3 3 0 0 0-3-3H2Z" />
    <path d="M22 5h-6a4 4 0 0 0-4 4v11a3 3 0 0 1 3-3h7Z" />
  </svg>
);

/* Closed book with a ribbon bookmark — ready-made books */
const ReadyMadeIcon: React.FC = () => (
  <svg {...iconProps}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
    <path d="M10 2v8l3-2.2L16 10V2" />
  </svg>
);

/* ─── Hero character icons ───
 * Drawn as SVG rather than emoji: 🐉/🚀/🐱 render with completely
 * different art (and sometimes as tofu) across iOS, Android and Windows,
 * so the chips looked inconsistent per platform. DragonIcon and
 * AstronautIcon live in common/CustomIcons so the story generator's
 * character-type chips can use the same drawings.
 */

/* Shared chevron for secondary links and ladder CTAs */
const ArrowIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/* ───── Component ───── */
export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const { t } = useTranslation();

  const [featuredBooks, setFeaturedBooks] = useState<FeaturedBook[]>([]);
  const [booksLoading, setBooksLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<string>('all');

  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);

  // Hero story-setup preview: the two questions you'd actually ask your child
  const [childName, setChildName] = useState('');
  const [heroCharacter, setHeroCharacter] = useState<string>('dragon');

  // Add-review modal state
  const [showModal, setShowModal] = useState(false);
  const [newReviewText, setNewReviewText] = useState('');
  const [newRating, setNewRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const booksRef = useRef<HTMLElement>(null);
  const reviewsRef = useRef<HTMLElement>(null);

  const faqItems = [
    {
      question: t('homePage.faq.q1', 'What is Storify Now?'),
      answer: t('homePage.faq.a1V3', 'Storify Now is a way to write bedtime stories together with your child. You sit down side by side, they decide who the hero is and what happens, and we turn those answers into a unique illustrated story in under a minute. Read it free online — and when you have favourites, order them as a printed book delivered to your door.')
    },
    {
      question: t('homePage.faq.q2V3', 'Do I need my child there to use it?'),
      answer: t('homePage.faq.a2V3', 'That\'s the whole idea — the questions are written so you can ask them out loud and let your child answer. Nothing stops you from creating a story on your own, but most families find the choosing part is what their kids enjoy most.')
    },
    {
      question: t('homePage.faq.q3', 'Can I read the stories online?'),
      answer: t('homePage.faq.a3', 'Absolutely! Every story you create is saved in your personal library and can be read anytime on any device. You can also explore stories created by other families in our public library.')
    },
    {
      question: t('homePage.faq.q4', 'Can I order a printed book?'),
      answer: t('homePage.faq.a4V3', 'Yes — three ways, in order of what families pick most. Print 5 to 10 stories you created yourself; mix in favourites from the public library if you don\'t have enough of your own yet; or order one of our ready-made books with no creating at all. All three arrive professionally printed and bound.')
    },
    {
      question: t('homePage.faq.q5', 'Do you have ready-made books?'),
      answer: t('homePage.faq.a5', 'We do! We offer a selection of beautifully crafted predefined storybooks that you can browse and order directly from our homepage. These are perfect as gifts or for families who want a great book without the creation step.')
    },
    {
      question: t('homePage.faq.q6', 'What languages are supported?'),
      answer: t('homePage.faq.a6', 'Stories can be created and read in English, Romanian, French, German, Spanish, and Italian. The entire platform interface is also available in all six languages.')
    },
    {
      question: t('homePage.faq.q7', 'Is it safe and age-appropriate for kids?'),
      answer: t('homePage.faq.a7', 'Every story is generated with child safety as a top priority. You select the age range (2–4, 4–6, 6–9, or 9–12), and the content, vocabulary, and themes are tailored accordingly. All stories are reviewed for age-appropriateness.')
    },
  ];

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index));
  };

  /*
   * Hero characters for the "who's the hero?" chips.
   * Each carries its own cover-title key — "in space" doesn't fit an
   * "{{name}} and the {{noun}}" template in most of our languages.
   */
  const heroCharacters = [
    {
      key: 'dragon',
      Icon: DragonIcon,
      label: t('homePage.hero.characterDragon', 'A dragon'),
      coverTitle: t('homePage.hero.coverTitleDragon', '{{name}} and the Dragon', {
        name: childName.trim() || t('homePage.hero.namePlaceholderName', 'Emma'),
      }),
    },
    {
      key: 'space',
      Icon: AstronautIcon,
      label: t('homePage.hero.characterSpace', 'An astronaut'),
      coverTitle: t('homePage.hero.coverTitleSpace', '{{name}} Among the Stars', {
        name: childName.trim() || t('homePage.hero.namePlaceholderName', 'Emma'),
      }),
    },
    {
      key: 'kitten',
      Icon: Cat,
      label: t('homePage.hero.characterKitten', 'A little cat'),
      coverTitle: t('homePage.hero.coverTitleKitten', '{{name}} and the Little Cat', {
        name: childName.trim() || t('homePage.hero.namePlaceholderName', 'Emma'),
      }),
    },
  ];

  const COUNTRY_FLAGS: Record<string, string> = {
    Romania: '\u{1F1F7}\u{1F1F4}',
    Germany: '\u{1F1E9}\u{1F1EA}',
    France: '\u{1F1EB}\u{1F1F7}',
    Spain: '\u{1F1EA}\u{1F1F8}',
    Italy: '\u{1F1EE}\u{1F1F9}',
    'United Kingdom': '\u{1F1EC}\u{1F1E7}',
    'United States': '\u{1F1FA}\u{1F1F8}',
    Austria: '\u{1F1E6}\u{1F1F9}',
    Netherlands: '\u{1F1F3}\u{1F1F1}',
    Belgium: '\u{1F1E7}\u{1F1EA}',
    Switzerland: '\u{1F1E8}\u{1F1ED}',
    Poland: '\u{1F1F5}\u{1F1F1}',
    Hungary: '\u{1F1ED}\u{1F1FA}',
  };

  const availableCountries = useMemo(() => {
    const countrySet = new Set<string>();
    featuredBooks.forEach((book) => {
      book.allowedCountries?.forEach((c) => countrySet.add(c));
    });
    return Array.from(countrySet).sort();
  }, [featuredBooks]);

  const filteredBooks = useMemo(() => {
    if (selectedCountry === 'all') return featuredBooks;
    return featuredBooks.filter(
      (book) =>
        !book.allowedCountries ||
        book.allowedCountries.length === 0 ||
        book.allowedCountries.includes(selectedCountry)
    );
  }, [featuredBooks, selectedCountry]);

  /*
   * Aggregate rating derived from the loaded reviews.
   * NOTE: this is based on the reviews returned by the endpoint (limited set).
   * For a true site-wide average, add a lightweight endpoint like
   * GET /reviews/stats -> { average: 4.8, count: 214 } and use it here.
   */
  const aggregate = useMemo(() => {
    if (reviews.length === 0) return null;
    const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
    return {
      average: Math.round((sum / reviews.length) * 10) / 10,
      count: reviews.length,
    };
  }, [reviews]);

  /* Fetch featured books */
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const response = await fetch(`${API_URL}/books`);
        if (!response.ok) throw new Error('Failed to fetch books');
        const books = await response.json();
        setFeaturedBooks(
          books.map((book: any) => ({
            id: book._id || book.id,
            title: book.title,
            description: book.description || '',
            price: book.price,
            currency: book.currency,
            imageUrl: book.imageUrl,
            allowedCountries: book.allowedCountries,
          }))
        );
      } catch (error) {
        console.error('Error fetching featured books:', error);
      } finally {
        setBooksLoading(false);
      }
    };
    fetchBooks();
  }, []);

  /* Fetch reviews */
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const limit = isLoggedIn ? 5 : 6;
        const response = await fetch(`${API_URL}/reviews?limit=${limit}`);
        if (!response.ok) throw new Error('Failed to fetch reviews');
        const data = await response.json();
        setReviews(data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setReviewsLoading(false);
      }
    };
    fetchReviews();
  }, [isLoggedIn]);

  /* Submit review */
  const handleSubmitReview = async () => {
    setSubmitError('');

    if (!newReviewText.trim()) {
      setSubmitError(t('homePage.reviews.errorEmpty', 'Please write a review.'));
      return;
    }
    if (newRating === 0) {
      setSubmitError(t('homePage.reviews.errorRating', 'Please select a rating.'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          text: newReviewText.trim(),
          rating: newRating,
        }),
      });

      if (!response.ok) throw new Error('Failed to submit review');

      const savedReview: Review = await response.json();
      setReviews((prev) => [savedReview, ...prev.slice(0, 4)]);
      setSubmitSuccess(true);

      setTimeout(() => {
        setShowModal(false);
        setNewReviewText('');
        setNewRating(0);
        setSubmitSuccess(false);
      }, 1800);
    } catch (error) {
      console.error('Error submitting review:', error);
      setSubmitError(t('homePage.reviews.errorSubmit', 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const renderStars = (rating: number) =>
    Array.from({ length: 5 }, (_, i) => (
      <Star key={i} $filled={i < rating}>
        ★
      </Star>
    ));

  /*
   * Primary conversion action.
   * The child's name typed in the hero is carried into the create flow
   * (and preserved through registration via the query param) so the
   * visitor's first investment isn't lost.
   *
   * TODO(conversion): the bigger win is letting anonymous visitors start
   * the create flow and only requiring an account at save/checkout.
   * When the backend allows that, change the '/register' branch to '/create'.
   */
  const handlePrimaryCTA = () => {
    const nameParam = childName.trim()
      ? `?name=${encodeURIComponent(childName.trim())}`
      : '';
    if (isLoggedIn) {
      navigate(`/create${nameParam}`);
    } else {
      navigate(`/register${nameParam}`);
    }
  };

  /*
   * Print ladder, option 1: straight into "pick stories for a book" mode.
   * /book/checkout can't be linked directly — it reads its stories from
   * router state and bounces when empty (BookCheckout.tsx), so selection
   * has to happen in the library first.
   */
  const handlePrintOwnStories = () => {
    if (isLoggedIn) {
      navigate('/library', { state: { startBookSelection: true } });
    } else {
      navigate('/register');
    }
  };

  const scrollToRef = (ref: React.RefObject<HTMLElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const activeCharacter =
    heroCharacters.find((c) => c.key === heroCharacter) ?? heroCharacters[0];

  return (
    <HomeContainer>
      {/* ─── Hero ─── */}
      <HeroSection>
        <HeroContent>
          <HeroLabel>
            {t('homePage.hero.labelV3', 'Story time you make together')}
          </HeroLabel>
          {/*
            The headline sells the activity, not the personalization.
            "Your child is the hero" is what every competitor claims;
            "you write it together" is the actual product.
          */}
          <HeroTitle>
            {t(
              'homePage.hero.titleV3',
              "Tonight's bedtime story is one you and your child write together"
            )}
          </HeroTitle>
          <HeroSubtitle>
            {t(
              'homePage.hero.subtitleV3',
              'Ask them who the hero is, where it happens, what goes wrong. Storify Now turns your answers into a unique illustrated story in under a minute — read it free, and print your favourites into a real book.'
            )}
          </HeroSubtitle>

          {aggregate && (
            <RatingRow
              onClick={() => scrollToRef(reviewsRef)}
              aria-label={t('homePage.hero.ratingAria', 'Jump to parent reviews')}
            >
              <RatingStars aria-hidden="true">★★★★★</RatingStars>
              <span>
                {t('homePage.hero.ratedBy', '{{avg}} from parents like you', {
                  avg: aggregate.average.toFixed(1),
                })}
              </span>
            </RatingRow>
          )}

          <ButtonGroup>
            <Button variant="primary" size="large" onClick={handlePrimaryCTA}>
              {isLoggedIn
                ? t('homePage.hero.createStoryV3', 'Start a story together')
                : t('homePage.hero.getStartedV3', 'Start a story together')}
            </Button>
            {/* Make the signup incentive explicit — sets expectations for the credit model */}
            {!isLoggedIn && (
              <CreditsNote>
                {t('homePage.hero.creditsNote', '10 free story credits when you sign up')}
              </CreditsNote>
            )}
          </ButtonGroup>

          {/*
            The other two offerings, kept reachable but subordinate.
            Ranking is the whole point of this page — these must never
            compete visually with the primary CTA above.
          */}
          <SecondaryPaths>
            <SecondaryLink onClick={() => navigate('/library')}>
              {t('homePage.hero.secondaryRead', 'Just looking? Read stories free')}
              <ArrowIcon />
            </SecondaryLink>
            <SecondaryLink onClick={() => scrollToRef(booksRef)}>
              {t('homePage.hero.secondaryBooks', 'Want a book without creating one?')}
              <ArrowIcon />
            </SecondaryLink>
          </SecondaryPaths>

          <TrustPoints>
            <TrustPoint>{t('homePage.hero.trustTogether', 'Made together in minutes')}</TrustPoint>
            <TrustPoint>{t('homePage.hero.trustCredits', '10 free credits at signup')}</TrustPoint>
            <TrustPoint>{t('homePage.hero.trustRead', 'Read stories online free')}</TrustPoint>
            <TrustPoint>{t('homePage.hero.trustLanguages', '6 languages, ages 2–12')}</TrustPoint>
          </TrustPoints>
        </HeroContent>

        {/*
          The visual mirrors the first two questions of the real flow
          rather than only showing off the printed product.
        */}
        <HeroVisual>
          <BookCoverPreview aria-hidden="true">
            <CoverKicker>{t('homePage.hero.coverKicker', 'A Storify Now book')}</CoverKicker>
            <CoverTitle>{activeCharacter.coverTitle}</CoverTitle>
            <CoverStars>✦ ✦ ✦</CoverStars>
          </BookCoverPreview>

          <NameInputLabel htmlFor="hero-child-name">
            {t('homePage.hero.nameLabelV3', "Start like you would with your child — what's their name?")}
          </NameInputLabel>
          <NameInput
            id="hero-child-name"
            type="text"
            maxLength={24}
            value={childName}
            onChange={(e) => setChildName(e.target.value)}
            placeholder={t('homePage.hero.namePlaceholder', 'e.g. Emma')}
          />

          <HeroPrompt>
            {t('homePage.hero.characterPrompt', "Now ask them: who's the hero?")}
          </HeroPrompt>
          <HeroChipsRow>
            {heroCharacters.map((character) => (
              <HeroChip
                key={character.key}
                $active={character.key === activeCharacter.key}
                aria-pressed={character.key === activeCharacter.key}
                onClick={() => setHeroCharacter(character.key)}
              >
                <character.Icon />
                {character.label}
              </HeroChip>
            ))}
          </HeroChipsRow>

          <CoverCaption>
            {t('homePage.hero.coverCaptionV3', 'Read online free · Print it whenever you want')}
          </CoverCaption>
        </HeroVisual>
      </HeroSection>

      {/* ─── Proof strip ─── */}
      <ProofStrip>
        {aggregate && (
          <ProofItem>
            ★ {aggregate.average.toFixed(1)}{' '}
            {t('homePage.proof.rating', 'rated by parents')}
          </ProofItem>
        )}
        <ProofItem>{t('homePage.proof.credits', '10 free credits when you sign up')}</ProofItem>
        <ProofItem>{t('homePage.proof.togetherV3', 'A story you make together, in minutes')}</ProofItem>
        <ProofItem>{t('homePage.proof.print', 'Professionally printed & shipped')}</ProofItem>
        <ProofItem>{t('homePage.proof.languages', 'Available in 6 languages')}</ProofItem>
      </ProofStrip>

      {/* ─── How It Works ─── */}
      <Section>
        <SectionTitle>
          {t('homePage.howItWorks.titleV3', 'How you make a story together')}
        </SectionTitle>
        <SectionSubtitle>
          {t(
            'homePage.howItWorks.subtitleV3',
            "It's a five-minute activity you do side by side — they decide what happens, you type it in."
          )}
        </SectionSubtitle>
        <StepsGrid>
          <StepCard $index={0}>
            <StepNumber>1</StepNumber>
            <StepTitle>{t('homePage.howItWorks.step1TitleV3', 'Sit down together')}</StepTitle>
            <StepDescription>
              {t(
                'homePage.howItWorks.step1DescV3',
                "Ask your child: who's the hero? Where does it happen? What goes wrong? You put their answers in."
              )}
            </StepDescription>
          </StepCard>
          <StepCard $index={1}>
            <StepNumber>2</StepNumber>
            <StepTitle>{t('homePage.howItWorks.step2TitleV3', 'Watch it come to life')}</StepTitle>
            <StepDescription>
              {t(
                'homePage.howItWorks.step2DescV3',
                'A unique illustrated story appears in seconds — in your language, written for their age.'
              )}
            </StepDescription>
          </StepCard>
          <StepCard $index={2}>
            <StepNumber>3</StepNumber>
            <StepTitle>{t('homePage.howItWorks.step3TitleV3', 'Read it tonight')}</StepTitle>
            <StepDescription>
              {t(
                'homePage.howItWorks.step3DescV3',
                'Read it free on any device. It stays in your library forever — and can become a printed book.'
              )}
            </StepDescription>
          </StepCard>
        </StepsGrid>
        <SectionCTARow>
          <Button variant="primary" size="large" onClick={handlePrimaryCTA}>
            {t('homePage.howItWorks.ctaV3', 'Start a story together')}
          </Button>
          {!isLoggedIn && (
            <CreditsNote>
              {t('homePage.hero.creditsNote', '10 free story credits when you sign up')}
            </CreditsNote>
          )}
        </SectionCTARow>
      </Section>

      {/* ─── Popular stories ───
        Moved above the commerce sections: it exists to prove the output is
        good and that other families do this, not to compete with creating.
      */}
      <Section>
        <SectionTitle>
          {t('homePage.popularStoriesV3', 'Stories other families made')}
        </SectionTitle>
        <SectionSubtitle>
          {t(
            'homePage.popularStoriesSubtitleV3',
            'Read any of them free — every one started with a few answers from a child.'
          )}
        </SectionSubtitle>
        <StoryList
          title=""
          limit={6}
          showLoadMore={false}
          showFilters={false}
          compact={true}
          userId={user?.id}
          sortBy="likes"
          showLike={false}
        />
        <SectionCTARow>
          <Button variant="outline" onClick={() => navigate('/library')}>
            {t('homePage.viewAllStoriesV2', 'Read all stories free')}
          </Button>
        </SectionCTARow>
      </Section>

      {/* ─── Print ladder ───
        The three routes to a physical book, ranked. Your own stories are
        the headline; ready-made is the fallback for people who don't want
        to create anything.
      */}
      <Section>
        <SectionTitle>
          {t('homePage.print.title', 'Turn your stories into a real book')}
        </SectionTitle>
        <SectionSubtitle>
          {t(
            'homePage.print.subtitle',
            'Pick 5–10 favourites and we print, bind and ship them — a book with your child in it, on your shelf.'
          )}
        </SectionSubtitle>
        <LadderGrid>
          <LadderCard $featured $index={0}>
            <LadderBadge>{t('homePage.print.badge', 'Most loved')}</LadderBadge>
            <LadderIconWrap $featured>
              <OwnStoriesIcon />
            </LadderIconWrap>
            <LadderTitle $featured>
              {t('homePage.print.ownTitle', 'Your own stories, printed')}
            </LadderTitle>
            <LadderDesc>
              {t(
                'homePage.print.ownDesc',
                'Choose 5 to 10 stories you created together, pick hardcover or softcover, and we deliver the finished book to your door.'
              )}
            </LadderDesc>
            <LadderCTARow>
              <Button variant="primary" onClick={handlePrintOwnStories}>
                {t('homePage.print.ownCta', 'Choose my stories')}
              </Button>
              <LadderNote>
                {isLoggedIn
                  ? t('homePage.print.ownNote', 'Preview the whole book before you pay.')
                  : t('homePage.print.ownNoteAnon', 'Create a few stories first — signup is free.')}
              </LadderNote>
            </LadderCTARow>
          </LadderCard>

          <LadderCard $index={1}>
            <LadderStepLabel>{t('homePage.print.orLabel', 'Or')}</LadderStepLabel>
            <LadderIconWrap>
              <LibraryIcon />
            </LadderIconWrap>
            <LadderTitle>
              {t('homePage.print.libraryTitle', 'Add stories from the library')}
            </LadderTitle>
            <LadderDesc>
              {t(
                'homePage.print.libraryDesc',
                "Not enough of your own yet? Mix in favourites from stories other families made."
              )}
            </LadderDesc>
            <LadderLink onClick={() => navigate('/library')}>
              {t('homePage.print.libraryCta', 'Browse the library')}
              <ArrowIcon />
            </LadderLink>
          </LadderCard>

          <LadderCard $index={2}>
            <LadderStepLabel>{t('homePage.print.orLabel', 'Or')}</LadderStepLabel>
            <LadderIconWrap>
              <ReadyMadeIcon />
            </LadderIconWrap>
            <LadderTitle>
              {t('homePage.print.readyTitle', 'Start from a ready-made book')}
            </LadderTitle>
            <LadderDesc>
              {t(
                'homePage.print.readyDesc',
                'Already written and illustrated by us. Order as-is — ideal as a gift.'
              )}
            </LadderDesc>
            <LadderLink onClick={() => scrollToRef(booksRef)}>
              {t('homePage.print.readyCta', 'See our books')}
              <ArrowIcon />
            </LadderLink>
          </LadderCard>
        </LadderGrid>
      </Section>

      {/* ─── Ready-made books ─── */}
      <Section ref={booksRef} id="books">
        <SectionTitle>{t('homePage.books.titleV3', 'Ready-made books')}</SectionTitle>
        <SectionSubtitle>
          {t(
            'homePage.books.subtitleV3',
            'No creating needed — these are written and illustrated by us, and ship as they are. Ideal as a gift.'
          )}
        </SectionSubtitle>
        {booksLoading ? (
          <CarouselSkeleton aria-hidden="true" />
        ) : featuredBooks.length > 0 ? (
          <>
            {availableCountries.length > 1 && (
              <CountryFilterRow>
                <CountryFilterLabel>
                  {t('homePage.filter.label', 'Ships to:')}
                </CountryFilterLabel>
                <CountryChip
                  $active={selectedCountry === 'all'}
                  onClick={() => setSelectedCountry('all')}
                >
                  {t('homePage.filter.all', 'All countries')}
                </CountryChip>
                {availableCountries.map((country) => (
                  <CountryChip
                    key={country}
                    $active={selectedCountry === country}
                    onClick={() => setSelectedCountry(country)}
                  >
                    {COUNTRY_FLAGS[country] && <span>{COUNTRY_FLAGS[country]}</span>}
                    {country}
                  </CountryChip>
                ))}
              </CountryFilterRow>
            )}
            {filteredBooks.length > 0 ? (
              <BookCarousel books={filteredBooks} />
            ) : (
              <NoBooksMessage>
                {t('homePage.noBooksForCountry', 'No books available for this country yet.')}
              </NoBooksMessage>
            )}
          </>
        ) : (
          <NoBooksMessage>
            {t('homePage.noBooksAvailable', 'No books at the moment, please check back soon!')}
          </NoBooksMessage>
        )}
      </Section>

      {/* ─── Reviews (hidden entirely when there are none) ─── */}
      {!reviewsLoading && (reviews.length > 0 || isLoggedIn) && (
        <Section ref={reviewsRef}>
          <SectionTitle>{t('homePage.reviews.title', 'What Parents Say')}</SectionTitle>
          {aggregate && (
            <AggregateRating>
              <RatingStars aria-hidden="true">★★★★★</RatingStars>
              <span>
                {t('homePage.reviews.aggregate', '{{avg}} average from {{count}} reviews', {
                  avg: aggregate.average.toFixed(1),
                  count: aggregate.count,
                })}
              </span>
            </AggregateRating>
          )}
          <ReviewGrid>
            {reviews.map((review, index) => (
              <ReviewCard key={review._id} $index={index}>
                <QuoteIcon>"</QuoteIcon>
                <ReviewText>{review.text}</ReviewText>
                <ReviewAuthor>
                  <AuthorAvatar>{getInitials(review.userName)}</AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>{review.userName}</AuthorName>
                    <StarRow>{renderStars(review.rating)}</StarRow>
                  </AuthorInfo>
                </ReviewAuthor>
              </ReviewCard>
            ))}

            {/* Add-a-review card (logged-in only) */}
            {isLoggedIn && (
              <AddReviewCard $index={reviews.length} onClick={() => setShowModal(true)}>
                <AddIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </AddIcon>
                <AddLabel>{t('homePage.reviews.addReview', 'Share Your Experience')}</AddLabel>
              </AddReviewCard>
            )}
          </ReviewGrid>
        </Section>
      )}

      {/* ─── Add Review Modal ─── */}
      {showModal && (
        <ModalOverlay onClick={() => !submitting && setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            {submitSuccess ? (
              <SuccessMessage>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <p>{t('homePage.reviews.thankYou', 'Thank you for your review!')}</p>
              </SuccessMessage>
            ) : (
              <>
                <ModalTitle>{t('homePage.reviews.modalTitle', 'Write a Review')}</ModalTitle>

                <FormGroup>
                  <Label>{t('homePage.reviews.ratingLabel', 'Rating')}</Label>
                  <StarRatingInput>
                    {Array.from({ length: 5 }, (_, i) => (
                      <StarButton
                        key={i}
                        type="button"
                        $filled={i < (hoverRating || newRating)}
                        onMouseEnter={() => setHoverRating(i + 1)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setNewRating(i + 1)}
                      >
                        ★
                      </StarButton>
                    ))}
                  </StarRatingInput>
                </FormGroup>

                <FormGroup>
                  <Label>{t('homePage.reviews.reviewLabel', 'Your Review')}</Label>
                  <TextArea
                    value={newReviewText}
                    onChange={(e) => setNewReviewText(e.target.value)}
                    placeholder={t(
                      'homePage.reviews.placeholder',
                      'Tell us about your experience with Storify Now...'
                    )}
                    maxLength={500}
                  />
                </FormGroup>

                {submitError && <ErrorText>{submitError}</ErrorText>}

                <ModalActions>
                  <Button
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    disabled={submitting}
                    style={{ flex: 1 }}
                  >
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSubmitReview}
                    disabled={submitting}
                    style={{ flex: 1 }}
                  >
                    {submitting
                      ? t('common.submitting', 'Submitting...')
                      : t('homePage.reviews.submit', 'Submit Review')}
                  </Button>
                </ModalActions>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* ─── FAQ ─── */}
      <Section>
        <SectionTitle>{t('homePage.faq.title', 'Frequently Asked Questions')}</SectionTitle>
        <FAQGrid>
          {faqItems.map((item, index) => (
            <FAQItem key={index} $index={index}>
              <FAQQuestion
                $isOpen={openFaqIndex === index}
                onClick={() => toggleFaq(index)}
                aria-expanded={openFaqIndex === index}
              >
                {item.question}
                <FAQIcon $isOpen={openFaqIndex === index}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </FAQIcon>
              </FAQQuestion>
              <FAQAnswer $isOpen={openFaqIndex === index}>
                <FAQAnswerText>{item.answer}</FAQAnswerText>
              </FAQAnswer>
            </FAQItem>
          ))}
        </FAQGrid>
      </Section>

      {/* ─── Closing CTA ─── */}
      <ClosingCTA>
        <ClosingTitle>
          {t('homePage.closing.titleV3', 'What will the two of you write tonight?')}
        </ClosingTitle>
        <ClosingSubtitle>
          {t(
            'homePage.closing.subtitleV3',
            'Sit down together, answer a few questions, and read it at bedtime. Print it into a real book whenever you\'re ready.'
          )}
        </ClosingSubtitle>
        <ClosingButton onClick={handlePrimaryCTA}>
          {t('homePage.hero.getStartedV3', 'Start a story together')}
        </ClosingButton>
        <ClosingNote>
          {isLoggedIn
            ? t('homePage.closing.note', 'Free to preview — no payment needed to start.')
            : t('homePage.closing.noteCredits', 'Sign up free and get 10 story credits — no payment needed to start.')}
        </ClosingNote>
      </ClosingCTA>
    </HomeContainer>
  );
};